import React, { useState } from "react";
import { getAwgPerUsd } from "@/lib/api";
import {
  ChevronDown, ChevronRight, DollarSign, MessageSquareQuote,
  RefreshCw, Layers, Info,
} from "lucide-react";

// ---------------------------------------------------------------------------
// CommercialSection — collapsible per-item editor block that surfaces the
// commercial commerce fields (request_estimate, recurring_price/label,
// tier_ids scoping, replaces_base_recurring) plus a live Customer Flow
// Preview that mirrors what shoppers will see on the public configurator.
//
// Kept opt-in via the `fields` prop so different item types (tiers, add-ons,
// standalone items, kits) can enable the exact subset they support.
// ---------------------------------------------------------------------------

const RECURRING_LABELS = ["month", "year", "week", "quarter"];

const Chip = ({ selected, onClick, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
      selected
        ? "border-[#FF6600] bg-[#FF6600]/15 text-[#FF6600]"
        : "border-[#262626] text-[#A3A3A3] hover:border-[#FF6600]/60 hover:text-[#FF6600]"
    }`}
  >
    {children}
  </button>
);

const Badge = ({ tone = "muted", icon: Icon, children, testId }) => {
  const tones = {
    muted: "border-[#262626] bg-[#151515] text-[#A3A3A3]",
    orange: "border-[#FF6600]/40 bg-[#FF6600]/10 text-[#FF6600]",
    green: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-400",
    violet: "border-violet-500/40 bg-violet-500/10 text-violet-400",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${tones[tone] || tones.muted}`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </span>
  );
};

// Small live-preview strip — computes badges from the value shape.
export const CustomerFlowPreview = ({ value = {}, tiers = [], scope = null, testIdPrefix }) => {
  const isEstimate = !!value.request_estimate;
  const hasRecurring = Number(value.recurring_price || 0) > 0;
  const label = (value.recurring_label || "month").trim();
  const rec = Number(value.recurring_price || 0);
  const tierScopeIds = Array.isArray(value.tier_ids) ? value.tier_ids : [];
  const showTierScope = scope === "addon";
  const tierScoped = showTierScope && tierScopeIds.length > 0;
  const scopedTierNames = tierScoped
    ? tiers
        .filter((t) => tierScopeIds.includes(t.id))
        .map((t) => t.name || "(unnamed)")
    : [];
  const replaces = scope === "addon" && !!value.replaces_base_recurring;

  return (
    <div
      data-testid={testIdPrefix ? `${testIdPrefix}-flow-preview` : "customer-flow-preview"}
      className="rounded-lg border border-[#262626] bg-[#0F0F0F] p-3"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7A7A] mb-2 inline-flex items-center gap-1.5">
        <Info className="w-3 h-3" /> Customer Flow Preview
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {isEstimate ? (
          <Badge tone="amber" icon={MessageSquareQuote} testId={testIdPrefix ? `${testIdPrefix}-flow-estimate` : undefined}>
            Request Estimate
          </Badge>
        ) : (
          <Badge tone="green" icon={DollarSign} testId={testIdPrefix ? `${testIdPrefix}-flow-buy` : undefined}>
            Buy Online
          </Badge>
        )}
        {hasRecurring && !isEstimate && (
          <Badge tone="orange" icon={RefreshCw} testId={testIdPrefix ? `${testIdPrefix}-flow-recurring` : undefined}>
            Recurring Fee Active · AWG {rec.toLocaleString()}/{label}
          </Badge>
        )}
        {replaces && hasRecurring && (
          <Badge tone="violet" testId={testIdPrefix ? `${testIdPrefix}-flow-replaces` : undefined}>
            Replaces Base Recurring
          </Badge>
        )}
        {showTierScope && (
          tierScoped ? (
            <Badge tone="violet" icon={Layers} testId={testIdPrefix ? `${testIdPrefix}-flow-tier-scope` : undefined}>
              Tier-Specific · {scopedTierNames.join(", ") || "(none)"}
            </Badge>
          ) : (
            <Badge tone="muted" icon={Layers} testId={testIdPrefix ? `${testIdPrefix}-flow-all-tiers` : undefined}>
              All Tiers
            </Badge>
          )
        )}
      </div>
      {isEstimate && (
        <p className="mt-2 text-[11px] italic text-[#A3A3A3]">
          Selecting this on the public builder flips the whole cart into Quote Mode — no online price is shown, the CTA becomes "Request Estimate".
        </p>
      )}
    </div>
  );
};


// Sep 2026 USD-first display (Option A). Edits `price_usd` /
// `recurring_price_usd` — DISPLAY ONLY. The AWG price above remains the
// amount priced, taxed and charged. Shows the AWG equivalent of the USD
// list price and its % difference so drift is always visible.
const UsdCompare = ({ awg, usd }) => {
  const rate = getAwgPerUsd() || 1.79;
  if (!(Number(awg) > 0)) return null;
  const exact = Number(awg) / rate;
  if (!(Number(usd) > 0)) {
    return <span>No US$ list price — shown as rate conversion (US$ {Math.round(exact).toLocaleString()}).</span>;
  }
  const eq = Number(usd) * rate;
  const pct = ((eq - Number(awg)) / Number(awg)) * 100;
  const tone = Math.abs(pct) > 1 ? "text-amber-400" : "text-[#7A7A7A]";
  return (
    <span className={tone}>
      Exact: US$ {exact.toFixed(2)} · US$ {Number(usd).toLocaleString()} ≈ AWG {eq.toFixed(2)} ({pct >= 0 ? "+" : ""}{pct.toFixed(1)}% vs AWG {Number(awg).toLocaleString()})
    </span>
  );
};

const UsdDisplayPrice = ({ value, set, isEstimate, testId }) => {
  const awg = value.price ?? value.bundle_price ?? 0;
  const rec = Number(value.recurring_price || 0);
  const input = (field, v, tid) => (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#7A7A7A] font-semibold">US$</span>
      <input
        type="number"
        min="0"
        step="1"
        value={Number(v) > 0 ? v : ""}
        onChange={(e) => set({ [field]: Number(e.target.value) || 0 })}
        placeholder="—"
        disabled={isEstimate}
        data-testid={testId ? `${testId}-${tid}` : undefined}
        className="w-full bg-[#1A1A1A] border border-[#262626] rounded-xl pl-12 pr-3 py-2 text-sm focus:outline-none focus:border-[#FF6600] disabled:opacity-40 disabled:cursor-not-allowed"
      />
    </div>
  );
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[#A3A3A3] inline-flex items-center gap-1.5">
        <DollarSign className="w-3 h-3" /> US$ Display Price (optional)
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {input("price_usd", value.price_usd, "price-usd")}
        {rec > 0 ? input("recurring_price_usd", value.recurring_price_usd, "recurring-price-usd") : <div />}
      </div>
      <p className="mt-1 text-[11px] text-[#7A7A7A]" data-testid={testId ? `${testId}-usd-compare` : undefined}>
        <UsdCompare awg={awg} usd={value.price_usd} />
        {rec > 0 && (
          <>
            <br />
            Recurring: <UsdCompare awg={rec} usd={value.recurring_price_usd} />
          </>
        )}
      </p>
      <p className="mt-1 text-[11px] italic text-[#7A7A7A]">
        Display only — customers are always priced, taxed and charged from the AWG amount.
      </p>
    </div>
  );
};

// The main editable block.
// Props:
//   value    — current item object (contains request_estimate, recurring_price, recurring_label, tier_ids, replaces_base_recurring)
//   onChange — (patch) => void; patch is a partial object merged into the item
//   scope    — "tier" | "addon" | "item" | "kit"
//   tiers    — full list of tiers on the parent service (only used when scope === "addon")
//   testId   — stable base test-id (e.g. `addon-2-commercial`)
export const CommercialSection = ({ value = {}, onChange, scope, tiers = [], testId }) => {
  // Auto-open when any commercial field is already set so admins land on
  // populated data instead of a hidden strip.
  const hasAny =
    !!value.request_estimate ||
    Number(value.recurring_price || 0) > 0 ||
    (value.recurring_label || "") !== "" ||
    (Array.isArray(value.tier_ids) && value.tier_ids.length > 0) ||
    !!value.replaces_base_recurring ||
    Number(value.price_usd || 0) > 0;
  const [open, setOpen] = useState(hasAny);

  const supportsTierIds = scope === "addon";
  const supportsReplaces = scope === "addon";
  const showRecurring = true; // all four item types support recurring

  const isEstimate = !!value.request_estimate;
  const rec = Number(value.recurring_price || 0);
  const label = value.recurring_label || "";
  const tierIds = Array.isArray(value.tier_ids) ? value.tier_ids : [];

  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div
      data-testid={testId}
      className="mt-3 rounded-xl border border-[#262626] bg-[#0B0B0B]"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId ? `${testId}-toggle` : "commercial-toggle"}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#111]"
      >
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF6600] inline-flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          Commercial
        </span>
        <span className="flex items-center gap-1.5">
          {isEstimate && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400">
              Estimate
            </span>
          )}
          {!isEstimate && rec > 0 && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#FF6600]/40 bg-[#FF6600]/10 text-[#FF6600]">
              Recurring
            </span>
          )}
          {supportsTierIds && tierIds.length > 0 && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-400">
              Tier-Specific
            </span>
          )}
        </span>
      </button>

      {open && (
        <div className="p-3 pt-1 space-y-4 border-t border-[#1A1A1A]">
          {/* Request Estimate toggle */}
          <div>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={isEstimate}
                onChange={(e) => set({ request_estimate: e.target.checked })}
                data-testid={testId ? `${testId}-request-estimate` : undefined}
                className="w-4 h-4 accent-[#FF6600]"
              />
              <span className="inline-flex items-center gap-1.5">
                <MessageSquareQuote className="w-3.5 h-3.5 text-amber-400" />
                Request Estimate (no online price — quote flow)
              </span>
            </label>
            <p className="mt-1 text-[11px] text-[#7A7A7A] pl-6">
              When on, selecting this item on the public configurator hides the price and switches the CTA to "Request Estimate".
            </p>
          </div>

          {/* USD display list price — display only; AWG stays authoritative */}
          <UsdDisplayPrice value={value} set={set} isEstimate={isEstimate} testId={testId} />

          {/* Recurring fee */}
          {showRecurring && (
            <div>
              <div className="text-xs uppercase tracking-widest text-[#A3A3A3] inline-flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3" /> Recurring Fee (optional)
              </div>
              <div className="mt-2 grid grid-cols-[1fr,140px] gap-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#7A7A7A] font-semibold">AWG</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={rec || ""}
                    onChange={(e) => set({ recurring_price: Number(e.target.value) || 0 })}
                    placeholder="0"
                    disabled={isEstimate}
                    data-testid={testId ? `${testId}-recurring-price` : undefined}
                    className="w-full bg-[#1A1A1A] border border-[#262626] rounded-xl pl-12 pr-3 py-2 text-sm focus:outline-none focus:border-[#FF6600] disabled:opacity-40 disabled:cursor-not-allowed"
                  />
                </div>
                <select
                  value={label}
                  onChange={(e) => set({ recurring_label: e.target.value })}
                  disabled={isEstimate}
                  data-testid={testId ? `${testId}-recurring-label` : undefined}
                  className="bg-[#1A1A1A] border border-[#262626] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <option value="">per…</option>
                  {RECURRING_LABELS.map((v) => (
                    <option key={v} value={v}>per {v}</option>
                  ))}
                </select>
              </div>
              {isEstimate && (
                <p className="mt-1 text-[11px] italic text-[#7A7A7A]">
                  Recurring fee is disabled while Request Estimate is on — pricing is handled during the quote.
                </p>
              )}
              {supportsReplaces && (
                <label className="mt-2 inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!value.replaces_base_recurring}
                    disabled={isEstimate || rec <= 0}
                    onChange={(e) => set({ replaces_base_recurring: e.target.checked })}
                    data-testid={testId ? `${testId}-replaces-base` : undefined}
                    className="w-4 h-4 accent-[#FF6600]"
                  />
                  <span className={isEstimate || rec <= 0 ? "text-[#7A7A7A]" : ""}>
                    Replaces base tier's recurring fee (does not stack)
                  </span>
                </label>
              )}
            </div>
          )}

          {/* Tier scope — add-ons only */}
          {supportsTierIds && (
            <div>
              <div className="text-xs uppercase tracking-widest text-[#A3A3A3] inline-flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> Tier Scope
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <Chip
                  selected={tierIds.length === 0}
                  onClick={() => set({ tier_ids: [] })}
                  testId={testId ? `${testId}-tier-all` : undefined}
                >
                  All Tiers
                </Chip>
                <span className="text-[10px] text-[#4A4A4A] px-1">or specific:</span>
                {tiers.length === 0 && (
                  <span className="text-[11px] italic text-[#7A7A7A]">Add tiers first to enable per-tier scoping.</span>
                )}
                {tiers.map((t) => {
                  const on = tierIds.includes(t.id);
                  return (
                    <Chip
                      key={t.id}
                      selected={on}
                      onClick={() => {
                        const next = on
                          ? tierIds.filter((x) => x !== t.id)
                          : [...tierIds, t.id];
                        set({ tier_ids: next });
                      }}
                      testId={testId ? `${testId}-tier-${t.id}` : undefined}
                    >
                      {t.name || "(unnamed)"}
                    </Chip>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-[#7A7A7A]">
                Empty selection = shown for every tier. Pick one or more tiers to restrict this add-on to specific packages (e.g. Complete Brand System upgrades).
              </p>
            </div>
          )}

          <CustomerFlowPreview
            value={value}
            tiers={tiers}
            scope={scope}
            testIdPrefix={testId}
          />
        </div>
      )}
    </div>
  );
};

export default CommercialSection;
